/**
 * Geometrie voor vormpresets die zowel de editor-hulplijn als de geexporteerde
 * snijcontour gebruiken.
 *
 * Waarom een aparte, bewust kleine module: de eis is dat de snijcontour in het
 * geexporteerde bestand EXACT overeenkomt met de stippellijn die de klant in
 * de editor zag. Dat kan alleen betrouwbaar als beide kanten dezelfde functie
 * aanroepen in plaats van dezelfde formule twee keer over te typen. Deze
 * module levert die ene functie; `design-editor-modal.tsx` gebruikt hem voor
 * de cirkel-hulplijn (fabric.Ellipse), `src/lib/printcom/cut-contour-pdf.ts`
 * gebruikt hem voor de vector die in het PDF-bestand terechtkomt.
 */

export interface ShapeBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface EllipseParams {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

/**
 * Ellipsparameters voor de "Rond"-vormpreset, in welke lengte-eenheid de box
 * ook is uitgedrukt (px in de editor, mm in het exportbestand). Dezelfde
 * verhouding tot de box levert bij elke schaal dezelfde vorm op.
 */
export function getCircleEllipseParams(box: ShapeBox): EllipseParams {
  return {
    cx: box.left + box.width / 2,
    cy: box.top + box.height / 2,
    rx: box.width / 2,
    ry: box.height / 2,
  };
}

export interface RoundedRectParams extends ShapeBox {
  radius: number;
}

/**
 * Rechthoek met afgeronde hoeken voor de "Afgeronde hoeken"-vormpreset. De
 * straal wordt begrensd op de halve korte zijde (meer kan geometrisch niet);
 * de editor-hulplijn en de snijlijn in het PDF-bestand gebruiken allebei deze
 * functie, zodat ze dezelfde straal tonen.
 */
export function getRoundedRectParams(box: ShapeBox, radius: number): RoundedRectParams {
  const limit = Math.max(0, Math.min(box.width, box.height) / 2);
  const safeRadius = Number.isFinite(radius) ? Math.max(0, Math.min(radius, limit)) : 0;
  return { ...box, radius: safeRadius };
}
